import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * How to hire her.
 *
 * This file existed for the whole life of the project and was imported by
 * nothing, which meant the site never once said what she sells. Two of the
 * three revenue paths ran through here, and both were at zero.
 *
 * Rewritten to lead with what the client ends up with rather than with her
 * process. The four fee and engagement strings that used to sit here are gone:
 * they were contract terms in her name that no source supported. The section
 * ships without them rather than with invented ones.
 */
const OFFERS = [
  {
    id: "residence",
    title: "A home built around you",
    promise:
      "Ground up villas, apartments and second homes, where the joinery and the light are made for the room rather than ordered for it.",
    forWho: "You have a site",
    image: "/portfolio/img/77-02.jpg",
    getWhat: [
      "Architecture, interiors and furniture from one hand",
      "Every fitting drawn, not chosen from a catalogue",
      "Daylight, ventilation and running cost designed in",
    ],
    exemplar: { name: "77 East Villa", slug: "77-east-villa" },
  },
  {
    id: "restoration",
    title: "An old building, alive again",
    promise:
      "Restoration that keeps what makes a building worth keeping, and quietly rebuilds everything behind it.",
    forWho: "You own something old",
    image: "/portfolio/img/mt-00.jpg",
    getWhat: [
      "Original fabric kept, services rebuilt around it",
      "Local material, local craft, local hands",
      "Often the outcome is a retreat or a restaurant",
    ],
    exemplar: { name: "Mae Terra, Goa", slug: "mae-terra-goa" },
  },
  {
    id: "interiors",
    title: "Rooms in a house already built",
    promise:
      "Interiors, lighting and furniture for a house that is already standing. One trade, so there is nobody to blame at the seam.",
    forWho: "The rooms are still bare",
    image: "/portfolio/img/hrbr-03.jpg",
    getWhat: [
      "Interiors, lighting and furniture from one hand",
      "Every fitting drawn, not chosen from a catalogue",
      "Furniture and fittings made, not bought",
    ],
    exemplar: { name: "HRBR Villa", slug: "hrbr-villa" },
  },
  {
    id: "hospitality",
    title: "A venue that pays back",
    promise:
      "Cafes, bars and retreats. I run four of my own, so I judge a plan on covers and margin, not only on how it photographs.",
    forWho: "You are opening a place",
    image: "/portfolio/img/tv-01.jpg",
    getWhat: [
      "Concept, interiors and the full build out",
      "A floor plan costed in covers, not only in square feet",
      "A room that still works in its fourth year",
    ],
    exemplar: { name: "Tea Villa Cafe", slug: "tea-villa-cafe" },
  },
];

export const Services = () => (
  <section
    id="services"
    className="relative bg-cream text-ink py-24 md:py-32 border-t border-ink/15"
    data-testid="services-section"
  >
    <div className="mx-auto max-w-[1600px] px-6 md:px-10">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-10% 0px" }}
        transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
        className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-14 md:mb-20"
      >
        <div className="col-span-12 md:col-span-7">
          <p className="chapter-num">Work with the studio</p>
          <h2 className="mt-5 font-display text-display-l text-ink">
            Four ways to start.
          </h2>
        </div>
        <p className="col-span-12 md:col-span-4 md:col-start-9 md:text-right font-sans font-light text-base md:text-lg text-ink2 leading-relaxed">
          A small slate each year, so your project gets me rather than a team
          I am managing.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-10 md:gap-8">
        {OFFERS.map((s, i) => (
          <motion.article
            key={s.id}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-8% 0px" }}
            transition={{
              duration: 0.9,
              delay: i * 0.07,
              ease: [0.19, 1, 0.22, 1],
            }}
            className="group flex flex-col"
            data-testid={`service-${s.id}`}
          >
            {/* An architect's offer needs a picture. Four columns of grey body
                copy is a spec sheet, and it was the single biggest reason this
                section read as a template. */}
            <Link
              to={`/work/${s.exemplar.slug}`}
              className="img-frame aspect-[5/4] mb-7"
              data-cursor="view"
              aria-label={`${s.exemplar.name}, view project`}
              tabIndex={-1}
            >
              <img
                src={s.image}
                alt={s.exemplar.name}
                loading="lazy"
                width={1200}
                height={960}
              />
            </Link>

            <p className="label text-terracotta2">
              {s.forWho}
            </p>

            <h3 className="mt-3 font-display text-display-m text-ink max-w-[16ch]">
              {s.title}
            </h3>

            <p className="mt-4 font-sans font-light text-[15px] text-ink2 leading-relaxed">
              {s.promise}
            </p>

            <ul className="mt-6 space-y-2 flex-1">
              {s.getWhat.map((g) => (
                <li
                  key={g}
                  className="border-t border-ink/15 pt-2 font-sans font-light text-[14px] text-ink2 leading-snug"
                >
                  {g}
                </li>
              ))}
            </ul>

            <div className="mt-8 pt-5 border-t border-ink flex flex-wrap items-center justify-between gap-4">
              <Link
                to={`/work/${s.exemplar.slug}`}
                data-testid={`service-exemplar-${s.id}`}
                data-cursor="hover"
                className="label text-ink2 hover:text-ink transition-colors editorial-link"
              >
                See {s.exemplar.name}
              </Link>
              <Link
                to="/contact"
                data-testid={`service-cta-${s.id}`}
                data-cursor="hover"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-ink text-cream label hover:bg-terracotta transition-colors duration-500"
              >
                Start here
                <ArrowUpRight
                  size={12}
                  className="transition-transform duration-300 group-hover:rotate-45"
                />
              </Link>
            </div>
          </motion.article>
        ))}
      </div>

      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9 }}
        className="mt-14 md:mt-20 font-display text-2xl md:text-3xl text-ink2 max-w-[44ch]"
      >
        If yours is none of these, write anyway. I will tell you honestly
        whether it is a fit.
      </motion.p>
    </div>
  </section>
);
